/**
 * Partie adverse — modèle « vrais dossiers » (cutover 2026-07, migration 143).
 *
 * Design : dossier = RANGEMENT, tag = SÉMANTIQUE.
 *   - Chaque dossier racine de la catégorie `partie_adverse` est un VRAI
 *     dossier (`is_folder = true`) qui porte le tag de son TYPE dans
 *     `document_subcategory` ('actes_communiques' | 'pieces_communiquees').
 *   - Les documents héritent du tag de leur dossier racine à l'upload/au
 *     déplacement — les lecteurs backend (RAG, case_data_loader,
 *     jurisprudence, agent) interrogent la sémantique via ce tag et ne
 *     dépendent JAMAIS du nom du dossier.
 *   - La paire canonique (« Écritures adverses » / « Pièces adverses ») est
 *     auto-créée, non renommable, non supprimable. Les paires supplémentaires
 *     (« Écritures adverses 2 »…) créées par « Ajouter une partie adverse »
 *     sont renommables et supprimables : la dérivation du type passe par le
 *     TAG de la ligne dossier, pas par le nom (robuste au renommage).
 */

import { adverseKindOfRootFolder, PARTIE_ADVERSE_PAIR, type PartieAdverseSubcategory } from '@hector/shared-types';
import type { FolderNode } from '@/services/folderHierarchyService';

/**
 * Marqueur `document_type` des dossiers canoniques auto-créés. Identité DB
 * stable partagée avec la migration 143 (idempotence : le script ne recrée
 * pas un canonique déjà présent, qu'il vienne de la migration ou du frontend).
 */
export const SYSTEM_FOLDER_DOCUMENT_TYPE = 'system_folder';

/** Nom d'un dossier de paire pour un index donné (1 = canonique, sans suffixe). */
export function pairFolderName(baseName: string, index: number): string {
  return index <= 1 ? baseName : `${baseName} ${index}`;
}

/**
 * Premier index de paire libre pour « Ajouter une partie adverse ».
 * Scanne les noms des dossiers racine : « Écritures adverses 3 » ⇒ 3 occupé.
 * L'index 1 (canonique, sans suffixe) est toujours occupé ⇒ retour ≥ 2.
 * Un même index est réputé occupé si L'UN des deux noms de la paire l'occupe
 * (paire partiellement supprimée : on n'y réattribue pas de nouveaux dossiers).
 */
export function nextPairIndex(rootFolderNames: readonly string[]): number {
  let max = 1;
  for (const name of rootFolderNames) {
    for (const spec of PARTIE_ADVERSE_PAIR) {
      if (name === spec.name) continue; // canonique = index 1, déjà compté
      const m = name.match(new RegExp(`^${escapeRegExp(spec.name)} (\\d+)$`));
      if (m) max = Math.max(max, parseInt(m[1]!, 10));
    }
  }
  return max + 1;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Index de partie d'un nom de dossier de paire NON RENOMMÉ : canonique = 1,
 * « Écritures/Pièces adverses N » = N, tout autre nom = undefined. Sert au
 * calcul du prochain index (`nextPairIndex`) et de SIGNAL de paire pour les
 * dossiers pré-seed — le groupement, lui, ne dépend pas des noms (renommage
 * libre depuis la spec 2026-07-17).
 */
export function pairIndexFromName(name: string): number | undefined {
  for (const spec of PARTIE_ADVERSE_PAIR) {
    if (name === spec.name) return 1;
    const m = name.match(new RegExp(`^${escapeRegExp(spec.name)} (\\d+)$`));
    if (m) return parseInt(m[1]!, 10);
  }
  return undefined;
}

/**
 * Dossier canonique par MARQUEUR (rename-proof) : `document_type =
 * 'system_folder'` à la racine. C'est l'identité DB partagée avec la
 * migration 143 — le nom, lui, est renommable par l'avocat.
 */
export function isSystemPartieAdverseFolder(folder: Pick<FolderNode, 'path' | 'documentType'>): boolean {
  return folder.path.length === 1 && folder.documentType === SYSTEM_FOLDER_DOCUMENT_TYPE;
}

/** Libellé court du type, affiché en badge sur les cartes (rename-proof, dérivé du TAG). */
export function kindShortLabel(kind: PartieAdverseSubcategory): string {
  return kind === 'actes_communiques' ? 'Écritures' : 'Pièces';
}

/** Section d'affichage de la racine de l'onglet Partie adverse. */
export interface PartieAdverseSectionGroup {
  key: string;
  label: string;
  folders: FolderNode[];
  /**
   * true sur la dernière section de PARTIE : « Ajouter une partie adverse »
   * s'affiche à côté de sa paire (spec 2026-07-17).
   */
  addTrailing: boolean;
}

/**
 * Groupe les dossiers RACINE de l'onglet en sections « Partie adverse i »,
 * écritures avant pièces dans chaque section (ordre par TAG, jamais par nom).
 * Le groupement est RENAME-PROOF (spec 2026-07-17, renommage libre) :
 *   - Partie 1 = la paire canonique, reconnue par son MARQUEUR system_folder.
 *   - Les paires suivantes = dossiers racine porteurs d'un `sibling_order`
 *     (seedé à la création de la paire, jamais réécrit — le drag-reorder est
 *     neutralisé dans cet onglet) ou d'un nom de paire non renommé. Appariement
 *     par ADJACENCE dans l'ordre de seed/création : on ouvre une nouvelle
 *     partie dès qu'un type se répète (une partie = au plus un dossier de
 *     chaque type — survit à la suppression d'un seul dossier d'une paire).
 *   - Les labels sont SÉQUENTIELS (position), pas parsés des noms.
 * Les dossiers racine restants (legacy pré-migration 143 : tagués mais sans
 * sibling_order ni nom de paire) tombent dans « Autres dossiers ».
 */
export function groupRootFoldersByPartie(folders: FolderNode[]): PartieAdverseSectionGroup[] {
  const kindRank = (f: FolderNode) => (adverseKindOfRootFolder(f) === 'actes_communiques' ? 0 : 1);

  const canonical: FolderNode[] = [];
  const pairFolders: FolderNode[] = [];
  const others: FolderNode[] = [];
  for (const folder of folders) {
    if (isSystemPartieAdverseFolder(folder)) canonical.push(folder);
    else if (folder.siblingOrder != null || pairIndexFromName(folder.name) !== undefined) pairFolders.push(folder);
    else others.push(folder);
  }

  // Appariement par adjacence, dans l'ordre stable seed/création.
  pairFolders.sort((a, b) => {
    const soA = a.siblingOrder ?? Number.POSITIVE_INFINITY;
    const soB = b.siblingOrder ?? Number.POSITIVE_INFINITY;
    if (soA !== soB) return soA - soB;
    return a.createdAt.localeCompare(b.createdAt);
  });
  const pairGroups: FolderNode[][] = [];
  for (const folder of pairFolders) {
    const current = pairGroups[pairGroups.length - 1];
    const kind = adverseKindOfRootFolder(folder);
    if (current && !current.some((f) => adverseKindOfRootFolder(f) === kind)) current.push(folder);
    else pairGroups.push([folder]);
  }

  const partieGroups = [
    ...(canonical.length > 0 ? [canonical] : []),
    ...pairGroups,
  ];
  const sections: PartieAdverseSectionGroup[] = partieGroups.map((group, i) => ({
    key: `partie-${i + 1}`,
    label: partieGroups.length === 1 ? 'Partie adverse' : `Partie adverse ${i + 1}`,
    folders: group.slice().sort((a, b) => kindRank(a) - kindRank(b)),
    addTrailing: i === partieGroups.length - 1,
  }));
  if (others.length > 0) {
    sections.push({
      key: 'autres',
      label: 'Autres dossiers',
      folders: others,
      // Pas de paire encore créée (racine legacy pure) : le bouton doit
      // quand même exister quelque part.
      addTrailing: sections.length === 0,
    });
  }
  return sections;
}

/** Libellé utilisateur d'un kind. */
export function kindLabel(kind: PartieAdverseSubcategory): string {
  return kind === 'actes_communiques' ? 'Écritures adverses' : 'Pièces adverses';
}

